import { useMemo, useState } from "react";
import axios from "axios";
import { CheckCircle2, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { API_BASE } from "@/lib/api";
import { serverMessage } from "./ReferCopy";

const EMPTY = {
  name: "", phone: "", role_code: "", experience: "", branches: [], note: "", consent: false,
  company_website: "",
};

const Label = ({ children, required }) => (
  <p className="mb-1.5 text-[14px] font-semibold">
    {children}{required && <span className="ml-0.5 text-destructive">*</span>}
  </p>
);

const field = "h-12 w-full rounded-md border bg-background px-3 text-[15px] outline-none focus-visible:ring-1 focus-visible:ring-ring";

/** The referral itself: who is being referred and nothing about who is referring them — that was
 *  settled at the gate. Built for a phone first: one column, big taps, no date pickers. */
export const ReferForm = ({ t, me, config, onSent }) => {
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [done, setDone] = useState(false);

  const branches = config?.branches || [];
  const allTicked = branches.length > 0 && f.branches.length === branches.length;
  const set = (k) => (v) => setF((prev) => ({ ...prev, [k]: v }));

  const toggleBranch = (code) => setF((prev) => ({
    ...prev,
    branches: prev.branches.includes(code)
      ? prev.branches.filter((c) => c !== code)
      : [...prev.branches, code],
  }));

  const ready = useMemo(() => (
    f.name.trim().length > 1 && /^[6-9]\d{9}$/.test(f.phone) && f.role_code && f.experience
    && f.branches.length > 0 && f.consent
  ), [f]);

  const submit = async () => {
    setBusy(true); setError(null);
    try {
      await axios.post(`${API_BASE}/public/refer`, {
        emp_id: me.emp_id, referrer_phone: me.phone,
        name: f.name, phone: f.phone, role_code: f.role_code, experience: f.experience,
        branches: f.branches, note: f.note, consent: f.consent,
        company_website: f.company_website,
      });
      setDone(true);
      onSent?.();
    } catch (e) {
      setError(serverMessage(e?.response?.data?.detail, t));
    } finally { setBusy(false); }
  };

  if (done) {
    return (
      <div className="rounded-2xl border bg-card p-7 text-center" data-testid="refer-thanks">
        <CheckCircle2 className="mx-auto h-11 w-11 text-emerald-600" />
        <h2 className="mt-3 font-display text-2xl font-bold">{t.thanksTitle}</h2>
        <p className="mt-2 text-sm text-muted-foreground">{t.thanksBody}</p>
        <Button className="mt-5 h-11 bg-emerald-600 text-white hover:bg-emerald-700"
          onClick={() => { setF(EMPTY); setDone(false); }} data-testid="refer-another">
          {t.another}
        </Button>
      </div>
    );
  }

  return (
    <>
      <section className="rounded-2xl border bg-card p-5" data-testid="refer-referred-by">
        <h2 className="text-base font-bold md:text-lg">{t.referredBy}</h2>
        <div className="mt-2.5 rounded-xl border bg-muted/40 px-3 py-2.5">
          <p className="text-[14px] font-semibold">{me.emp_id} · {me.name}</p>
          <p className="text-[12px] text-muted-foreground">{me.role} · {me.branch}</p>
        </div>
      </section>

      <section className="mt-4 rounded-2xl border bg-card p-5" data-testid="refer-candidate">
        <h2 className="text-base font-bold md:text-lg">{t.candidateTitle}</h2>

        <div className="mt-3 grid gap-4 sm:grid-cols-2">
          <div>
            <Label required>{t.fullName}</Label>
            <Input value={f.name} onChange={(e) => set("name")(e.target.value)}
              className="h-12 text-[15px]" data-testid="refer-candidate-name" />
          </div>
          <div>
            <Label required>{t.mobile}</Label>
            <Input
              value={f.phone} inputMode="numeric" maxLength={10}
              onChange={(e) => set("phone")(e.target.value.replace(/\D/g, "").slice(0, 10))}
              placeholder={t.phoneHint} className="h-12 text-[15px]"
              data-testid="refer-candidate-phone"
            />
          </div>
          <div>
            <Label required>{t.role}</Label>
            <select value={f.role_code} onChange={(e) => set("role_code")(e.target.value)}
              className={field} data-testid="refer-candidate-role">
              <option value="">{t.pickRole}</option>
              {(config?.roles || []).map((r) => (
                <option key={r.code} value={r.code}>{r.label}</option>
              ))}
            </select>
            <p className="mt-1 text-[11px] text-muted-foreground">{t.roleNote}</p>
          </div>
          <div>
            <Label required>{t.experience}</Label>
            <select value={f.experience} onChange={(e) => set("experience")(e.target.value)}
              className={field} data-testid="refer-candidate-exp">
              <option value="">{t.pickExperience}</option>
              {(config?.experience || []).map((x) => (
                <option key={x.code} value={x.code}>
                  {t.experienceLabels[x.code] || x.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="mt-4">
          <Label required>{t.branches}</Label>
          <p className="-mt-1 mb-2 text-[12px] text-muted-foreground">
            {t.branchesHint}
            {f.branches.length > 0 && (
              <span className="ml-1.5 font-medium text-emerald-700 dark:text-emerald-400"
                data-testid="refer-branches-count">
                · {t.ticked.replace("{n}", f.branches.length)}
              </span>
            )}
          </p>
          <label className="flex cursor-pointer items-center gap-2.5 rounded-t-xl border border-b-0 bg-muted/40 px-3 py-3">
            <Checkbox
              checked={allTicked}
              onCheckedChange={() => set("branches")(allTicked ? [] : branches.map((b) => b.code))}
              data-testid="refer-branch-all"
            />
            <span className="text-[14px] font-semibold">{t.allBranches}</span>
          </label>
          <div className="max-h-64 overflow-y-auto rounded-b-xl border" data-testid="refer-branches">
            {branches.map((b) => (
              <label key={b.code}
                className="flex cursor-pointer items-center gap-2.5 border-b px-3 py-3 last:border-b-0 hover:bg-muted/40">
                <Checkbox
                  checked={f.branches.includes(b.code)}
                  onCheckedChange={() => toggleBranch(b.code)}
                  data-testid={`refer-branch-${b.code}`}
                />
                <span className="text-[14px] leading-tight">{b.name}</span>
              </label>
            ))}
          </div>
        </div>

        <div className="mt-4">
          <Label>
            {t.note} <span className="font-normal text-muted-foreground">({t.optional})</span>
          </Label>
          <Textarea value={f.note} onChange={(e) => set("note")(e.target.value)}
            placeholder={t.notePlaceholder} rows={3} maxLength={400}
            className="text-[15px]" data-testid="refer-candidate-note" />
        </div>

        {/* The honeypot: off-screen, never seen, never stored when filled. */}
        <input
          tabIndex={-1} autoComplete="off" aria-hidden="true"
          className="absolute left-[-9999px] h-0 w-0 opacity-0"
          value={f.company_website} onChange={(e) => set("company_website")(e.target.value)}
        />

        <label className="mt-4 flex cursor-pointer items-start gap-3 rounded-xl border p-3.5">
          <Checkbox checked={f.consent} onCheckedChange={(v) => set("consent")(!!v)}
            data-testid="refer-consent" className="mt-0.5" />
          <span className="text-[13px] leading-relaxed">
            {t.consent}<span className="ml-0.5 text-destructive">*</span>
          </span>
        </label>

        {error && (
          <p className="mt-3 rounded-lg border border-destructive/40 bg-destructive/10 px-3 py-2 text-[13px] text-destructive"
            data-testid="refer-error">{error}</p>
        )}

        <Button
          className="mt-4 h-12 w-full bg-emerald-600 text-[15px] font-semibold text-white hover:bg-emerald-700"
          disabled={!ready || busy} onClick={submit} data-testid="refer-submit"
        >
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : t.submit}
        </Button>
      </section>
    </>
  );
};
